// Pure, surface-agnostic logic for the spatial export dialog — defaults, validation,
// the attribute trigger text, the submit payload, and the success-toast summary. Kept
// separate from the component so it is trivially unit-testable.

import type {
  SpatialExportSelection,
  SpatialFormat,
  SpatialLayerOption,
} from './types';

/**
 * The combined feature count above which the spatial export is blocked — a HARD cap, mirroring the
 * backend's GisExport::Sources::Base::MAX_FEATURES. An over-cap export cannot complete (it would
 * stream millions of ids into a job that times out the browser), so the dialog refuses it up front
 * and asks the user to narrow, rather than starting a doomed job; the backend enforces the same cap
 * authoritatively. Passed to the dialog as `maxFeatures`.
 */
export const SPATIAL_EXPORT_MAX_FEATURES = 100_000;

/** Shapefile is the only format checked on open (product spec AC 2). */
export const DEFAULT_FORMAT: SpatialFormat = 'shapefile';

/** The format checkboxes, in display order. */
export const FORMAT_OPTIONS: readonly { id: SpatialFormat; label: string }[] = [
  { id: 'geojson', label: 'GeoJSON' },
  { id: 'shapefile', label: 'Shapefile' },
  { id: 'kml', label: 'KML' },
];

/**
 * The default state on every open: every layer, all its attributes, Shapefile only.
 *
 * @param {SpatialLayerOption[]} layers - The roster.
 * @return {{ layerIds: string[]; attributesByLayer: Record<string, string[]>; formats: SpatialFormat[] }} The default state.
 */
export function defaultSelection(layers: SpatialLayerOption[]): {
  layerIds: string[];
  attributesByLayer: Record<string, string[]>;
  formats: SpatialFormat[];
} {
  return {
    layerIds: layers.map((layer) => layer.id),
    attributesByLayer: Object.fromEntries(
      layers.map((layer) => [
        layer.id,
        layer.attributes.map((attribute) => attribute.id),
      ])
    ),
    formats: [DEFAULT_FORMAT],
  };
}

/**
 * The hint shown while Export is disabled because the spatial selection is incomplete (no layer or no
 * format). The single source of truth for both the shared dialog and the Eval 3 content-export modal,
 * so the copy can't drift between them.
 */
export const SPATIAL_EXPORT_VALIDATION_HINT =
  'Select at least one layer and one format to export.';

/**
 * Export is allowed once at least one layer and at least one format are selected. The arrays are
 * `readonly string[]` (not `SpatialFormat[]`) so both the shared dialog and the Eval 3 content-export
 * modal — whose spatial config types its formats as plain `string[]` — share this one rule rather than
 * re-encoding it; only `.length` is read, so the element type is immaterial.
 *
 * @param {readonly string[]} layerIds - The selected layer ids.
 * @param {readonly string[]} formats - The selected formats.
 * @return {boolean} Whether the export can be submitted.
 */
export function canExport(
  layerIds: readonly string[],
  formats: readonly string[]
): boolean {
  return layerIds.length > 0 && formats.length > 0;
}

/**
 * The combined KNOWN feature count of the selected layers (a layer whose live total hasn't loaded yet
 * contributes 0 — see exceedsFeatureCap, which lets an unknown-count layer through to the backend guard).
 *
 * @param {SpatialLayerOption[]} layers - The roster.
 * @param {string[]} layerIds - The currently selected layer ids.
 * @return {number} The combined known feature count.
 */
export function selectedFeatureCount(
  layers: SpatialLayerOption[],
  layerIds: string[]
): number {
  return layers
    .filter((layer) => layerIds.includes(layer.id))
    .reduce((sum, layer) => sum + (layer.count ?? 0), 0);
}

/**
 * Whether the selected layers' combined feature count exceeds the hard cap, so the export must be
 * blocked. Only a KNOWN total can exceed it — an unknown-count layer contributes 0, so it is let
 * through here and the backend (the authoritative guard) rejects an over-cap export within seconds.
 * No cap supplied ⇒ never blocked (surfaces whose populations are always small).
 *
 * @param {SpatialLayerOption[]} layers - The roster.
 * @param {string[]} layerIds - The currently selected layer ids.
 * @param {number} [cap] - The hard feature cap; omitted means never block.
 * @return {boolean} Whether the selection exceeds the cap and must be blocked.
 */
export function exceedsFeatureCap(
  layers: SpatialLayerOption[],
  layerIds: string[],
  cap?: number
): boolean {
  if (cap === undefined) return false;
  return selectedFeatureCount(layers, layerIds) > cap;
}

/**
 * Assembles the submit payload from the dialog's state.
 *
 * @param {string[]} layerIds - The selected layer ids.
 * @param {Record<string, string[]>} attributesByLayer - Selected attribute ids per layer.
 * @param {SpatialFormat[]} formats - The selected formats.
 * @return {SpatialExportSelection} The selection payload.
 */
export function buildSelection(
  layerIds: string[],
  attributesByLayer: Record<string, string[]>,
  formats: SpatialFormat[]
): SpatialExportSelection {
  return {
    layers: layerIds.map((id) => ({
      id,
      attributeIds: attributesByLayer[id] ?? [],
    })),
    formats,
  };
}
